import { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import type { DraggableStateSnapshot } from '@hello-pangea/dnd';

interface DraggablePortalProps {
  snapshot: DraggableStateSnapshot;
  children: ReactNode;
}

/**
 * Renders a `<Draggable>`'s child into `<body>` while it is being dragged.
 *
 * `@hello-pangea/dnd` lifts the dragged item with `position: fixed` plus
 * viewport-relative `top`/`left` (see its `view/draggable/get-style.ts`). Our
 * global glass styling in `unified-main.tsx` sets `backdrop-filter` on every
 * non-solid `.MuiCard-root`, and a `backdrop-filter` other than `none` makes
 * that element the containing block for fixed-position descendants. Every drag
 * list in the app lives inside such a Card, so the lifted row was positioned
 * relative to the Card instead of the viewport and jumped away from the cursor
 * by the Card's own offset.
 *
 * Portaling to `<body>` puts the item back in the viewport's coordinate space,
 * which keeps this correct no matter how many glass ancestors it sits under.
 */
export const DraggablePortal = ({ snapshot, children }: DraggablePortalProps) => {
  // Reparenting a node blurs whatever was focused inside it, and the drag
  // handle is focused for both keyboard and mouse lifts. The keyboard sensor
  // reads keys off the focused handle, so losing it strands the drag: arrows
  // and Escape stop working and the row is left mid-lift. dnd's own
  // `use-focus-marshal` restores focus across remounts, but a portal move is
  // not a remount, so it never fires here. Put focus back if the move dropped
  // it — and only then, so a deliberate focus elsewhere is left alone.
  useLayoutEffect(() => {
    if (!snapshot.isDragging) return;
    if (document.activeElement !== document.body) return;

    const dragged = document.body.querySelector<HTMLElement>(':scope > [data-rfd-draggable-id]');
    if (!dragged) return;

    const handle = dragged.matches('[data-rfd-drag-handle-draggable-id]')
      ? dragged
      : dragged.querySelector<HTMLElement>('[data-rfd-drag-handle-draggable-id]');
    handle?.focus();
  }, [snapshot.isDragging]);

  if (!snapshot.isDragging) {
    return <>{children}</>;
  }
  return createPortal(children, document.body);
};

export default DraggablePortal;
